import { Grid } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { FullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { QueryToSqlHelper } from '../../../../api/query-to-sql-helper';
import { getAliasForMetricGroupId } from '../../../api/alias/utils';
import { Hasher } from '../../../api/hasher';
import { DataValue, hashString } from '../../../api/types';
import { toRegularMetricResultOrError } from '../../../api/utils';
import { UNDEFINED_DISPLAY_KEY } from '../../../constants';
import {
  useAliasesContext,
  useGlobalLocaleContext,
  useLatestPreferencesContext,
  useMetricDetailsMapContext,
  useMetricsQueryServiceContext,
} from '../../../context/contexts';
import { GlobalDisplayState } from '../../../context/types';
import {
  DataFieldWithDataType,
  RegularMetricIdType,
  TimeSelection,
} from '../../../types';
import { CHART_CONTENT_HEIGHT, CHART_TITLE_HEIGHT } from '../../styles';
import { ManualDataRefetchCountHandle, MetricTypes } from '../../types';
import { ChartDataFetchError } from '../charts/ChartDataFetchError';
import { ChartTitle } from '../charts/ChartTitle';
import { useGetMetricDisplayName } from '../charts/hooks';
import { toMuiChartsTimePeriodData } from '../charts/mui-charts/utils-display';
import { useDisplayWithPersistence } from '../charts/timeperiod/hooks';
import { timeSliderStateToTimePeriodTimeSelection } from '../charts/timeperiod/utils';
import { TableViewTimePeriodComponentConfig } from '../dashboards/types';
import { Filter, Segment } from '../filter/filterbar/types';
import { ItemsState } from '../filter/types';
import { formatHierarchicalLabel } from '../filter/utils-display';
import { TimeSliderState } from '../timeslider/types';
import { TableViewMetrics } from './TableViewMetrics';
import { Tools } from './Tools';

interface TableViewTimePeriodProps {
  componentConfig: TableViewTimePeriodComponentConfig;
  manualDataRefetchCountHandle: ManualDataRefetchCountHandle;
  timeSliderState: TimeSliderState | null;
  filtersState: ItemsState<Filter>;
  segmentationState: ItemsState<Segment>;
  showTools: boolean;
  fullScreenHandle: FullScreenHandle;
}

export const TableViewTimePeriod = (props: TableViewTimePeriodProps) => {
  const {
    componentConfig,
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
    segmentationState,
    showTools,
    fullScreenHandle,
  } = props;
  const { t } = useTranslation();
  const metricService = useMetricsQueryServiceContext();

  const { title, query, chartTypeConfig } = componentConfig;
  const { metric, timeSelection, filters, segmentations } = query;
  const finalTimeSelection: TimeSelection = timeSliderState
    ? timeSliderStateToTimePeriodTimeSelection(timeSliderState)
    : timeSelection;

  const allFilters =
    [
      QueryToSqlHelper.getFilterConditions(
        filtersState.items,
        MetricTypes.REGULAR,
        false
      ),
      filters,
    ]
      .flatMap((f) => (f ? [f] : []))
      .join(' AND ') || undefined;

  const [queryKey] = useDebounce(
    JSON.stringify([
      'table-view-time-period',
      hashString([
        ...metricService.getDeps(),
        Hasher.hash([metric]),
        Hasher.hash(finalTimeSelection),
        Hasher.hash(allFilters),
        Hasher.hash(segmentations),
      ]),
      manualDataRefetchCountHandle.current,
    ]),
    500
  );

  const metricDetailsMap = useMetricDetailsMapContext();
  const domainPreferences = useLatestPreferencesContext();
  const aliases = useAliasesContext();
  const locale = useGlobalLocaleContext();
  const getAliasForMetric = getAliasForMetricGroupId(aliases, locale.selected);
  const formatLabel = (value: DataValue[], dataField: DataFieldWithDataType) =>
    formatHierarchicalLabel(value, dataField, t(UNDEFINED_DISPLAY_KEY), t);

  const { data, error } = useSuspenseQuery({
    queryKey: [queryKey],
    queryFn: () => {
      return toRegularMetricResultOrError(
        metricService.queryRegularMetrics,
        [metric] as RegularMetricIdType[],
        finalTimeSelection,
        allFilters,
        segmentations
      );
    },
  });

  const dummyGlobalDisplayState: GlobalDisplayState = {
    enablePercentage: false,
    showPercentage: false,
    enableStack: false,
    showStack: false,
    enableGridLines: false,
    showGridLines: false,
    enableLabels: false,
    showLabels: false,
    enableLegend: false,
    showLegend: false,
  };

  const displayHandle = useDisplayWithPersistence(
    componentConfig.chartId,
    chartTypeConfig,
    segmentations,
    [metric],
    dummyGlobalDisplayState
  );

  const getMetricDisplayName = useGetMetricDisplayName();

  if (error) {
    return (
      <ChartDataFetchError
        retry={() => manualDataRefetchCountHandle.increment()}
      />
    );
  }

  const [displayState] = displayHandle;

  const { tableViewData, metricSql } = toMuiChartsTimePeriodData(
    data,
    segmentations,
    displayState,
    metricDetailsMap,
    formatLabel,
    getMetricDisplayName,
    undefined,
    domainPreferences,
    t,
    undefined
  );

  return (
    <Grid item container direction="column" sx={{ height: '100%' }}>
      <Grid item container sx={{ height: CHART_TITLE_HEIGHT }}>
        <ChartTitle
          metrics={[metric]}
          metricsSql={metricSql}
          title={title?.[locale.selected]}
          timeSelection={finalTimeSelection}
          segmentations={segmentations}
          userFilters={allFilters}
        />
        {showTools ? <Tools fullScreenHandle={fullScreenHandle} /> : null}
      </Grid>
      <Grid item container sx={{ height: CHART_CONTENT_HEIGHT }}>
        <TableViewMetrics
          tableData={tableViewData}
          fullScreenHandle={fullScreenHandle}
          chartHeight={CHART_CONTENT_HEIGHT}
        />
      </Grid>
    </Grid>
  );
};
